import {createRouter, createWebHistory} from 'vue-router'
import {
    canRefreshAccessToken,
    clearAuthStorage,
    getAccessToken,
    isAccessTokenExpired,
    getTokenName,
    refreshAccessToken
} from '../utils/auth.js'

const adminPageRoutes = [
    {
        path: '/',
        name: 'adminRoot',
        redirect: '/admin/pet'
    },
    {
        path: '/index',
        name: 'adminIndex',
        component: () => import('../views/admin/index/index.vue'),
        children: [
            {
                path: '/admin/petKind',
                name: 'petKindList',
                component: () => import('../views/admin/pet/petKindList.vue')
            },
            {
                path: '/admin/pet',
                name: 'petList',
                component: () => import('../views/admin/pet/petList.vue')
            },
            {
                path: '/admin/petProp',
                name: 'petPropPage',
                component: () => import('../views/admin/pet/petPropPage.vue')
            },
            {
                path: '/admin/petSet',
                name: 'petSet',
                component: () => import('../views/admin/pet/petSet.vue')
            }
        ]
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('../views/login/index.vue')
    },
    {
        path: '/:pathMatch(.*)*',
        name: 'NotFound',
        component: () => import('../views/admin/index/index.vue')
    }
]

const teacherPageRoutes = [
    {
        path: '/',
        name: 'teacherRoot',
        redirect: '/teacher/zoo'
    },
    {
        path: '/teacher/index',
        name: 'teacherIndex',
        component: () => import('../views/teacher/index/index.vue'),
        children: [
            {
                path: '/teacher/zoo',
                name: 'teacherZoo',
                component: () => import('../views/teacher/pet/zoo.vue')
            },
            {
                path: '/teacher/rank',
                name: 'teacherRank',
                component: () => import('../views/teacher/pet/rank.vue')
            },
            {
                path: '/teacher/hospital',
                name: 'teacherHospital',
                component: () => import('../views/teacher/pet/hospital.vue')
            }
        ]
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('../views/login/index.vue')
    }
]

const babyPageRoutes = [
    {
        path: '/',
        name: 'babyRoot',
        redirect: '/baby'
    },
    {
        path: '/baby',
        name: 'babyHome',
        component: () => import('../views/baby/index.vue')
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('../views/login/index.vue')
    }
]

const baseRoutes = [
    {
        path: '/',
        name: 'baseRoot',
        redirect: '/index'
    },
    {
        path: '/index',
        name: 'index',
        component: () => import('../views/index/index.vue')
    },
    {
        path: '/login',
        name: 'login',
        component: () => import('../views/login/index.vue')
    }
]

function generateRoutes() {
    const tokenName = getTokenName()
    if (tokenName === 'admin_token') {
        return adminPageRoutes
    }
    if (tokenName === 'teacher_token') {
        return teacherPageRoutes
    }
    if (tokenName === 'baby_token') {
        return babyPageRoutes
    }
    return baseRoutes
}

const router = createRouter({
    history: createWebHistory(),
    routes: generateRoutes()
})

export function refreshRoutes() {
    const tokenName = getTokenName()
    if (!tokenName) {
        return
    }
    const dynamicRouteNames = [
        'adminRoot',
        'adminIndex',
        'petKindList',
        'petList',
        'petPropPage',
        'petSet',
        'teacherRoot',
        'teacherIndex',
        'teacherZoo',
        'teacherRank',
        'teacherHospital',
        'babyRoot',
        'babyHome',
        'baseRoot',
        'index',
        'login',
        'NotFound'
    ]
    const newRoutes = generateRoutes()
    dynamicRouteNames.forEach(name => {
        if (router.hasRoute(name)) {
            router.removeRoute(name)
        }
    })
    newRoutes.forEach(route => {
        router.addRoute(route)
    })
}

router.beforeEach(async (to, from, next) => {
    if (to.path === '/login') {
        next()
        return
    }

    let tokenName = getTokenName()
    let token = getAccessToken()
    const shouldRefreshToken = (!token || isAccessTokenExpired(token)) && canRefreshAccessToken()

    if (shouldRefreshToken) {
        try {
            await refreshAccessToken()
            tokenName = getTokenName()
            token = getAccessToken()
        } catch (error) {
            next({path: '/login', replace: true})
            return
        }
    }

    if (!token) {
        clearAuthStorage()
        next({path: '/login', replace: true})
        return
    }

    if (to.path.startsWith('/admin') && tokenName !== 'admin_token') {
        next({path: '/login', replace: true})
        return
    }

    if (to.path.startsWith('/teacher') && tokenName !== 'teacher_token') {
        next({path: '/login', replace: true})
        return
    }

    if (to.path.startsWith('/baby') && tokenName !== 'baby_token') {
        next({path: '/login', replace: true})
        return
    }

    next()
})

export default router
